import { Link } from "react-router-dom";
import { ArrowRight, Code, Cloud, Brain, Database, RefreshCw, Settings, Layers, DollarSign, Shield, TestTube, Users, ChevronDown } from "lucide-react";
import { Button } from "@/components/ui/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";

const services = [
  {
    icon: Code,
    slug: "software-development",
    name: "Software Development",
    description: "Build scalable, efficient, and secure software solutions tailored to your business needs. Our experienced developers leverage modern technologies and best practices to deliver high-quality applications.",
    features: ["Custom Application Development", "API Development & Integration", "Mobile App Development", "Legacy System Modernization"]
  },
  {
    icon: RefreshCw,
    slug: "digital-transformation",
    name: "Digital Transformation",
    description: "Navigate your digital journey with confidence. We help organizations reimagine their business processes, customer experiences, and operational models through strategic technology adoption.",
    features: ["Business Process Automation", "Customer Experience Transformation", "Digital Strategy Consulting", "Change Management"]
  },
  {
    icon: Settings,
    slug: "managed-services",
    name: "Managed Services",
    description: "Focus on your core business while we handle your technology operations. Our managed services provide reliable, cost-effective IT management with 24/7 support.",
    features: ["Infrastructure Management", "Application Support", "Security Operations", "Performance Monitoring"]
  },
  {
    icon: Database,
    slug: "data-engineering",
    name: "Data Engineering",
    description: "Transform raw data into actionable insights. We build robust data pipelines, warehouses, and analytics platforms that power data-driven decision making.",
    features: ["Data Pipeline Development", "Data Warehouse Design", "ETL/ELT Solutions", "Real-time Analytics"]
  },
  {
    icon: Brain,
    slug: "ai-machine-learning",
    name: "AI & Machine Learning",
    description: "Unlock the power of artificial intelligence. From predictive analytics to natural language processing, we implement AI solutions that deliver measurable business value.",
    features: ["Predictive Analytics", "Natural Language Processing", "Computer Vision", "ML Model Development"]
  },
  {
    icon: Cloud,
    slug: "cloud-services",
    name: "Cloud Services",
    description: "Accelerate your cloud journey with expert guidance. Whether you're migrating, optimizing, or building cloud-native applications, we have the expertise to help.",
    features: ["Cloud Migration", "Cloud Architecture Design", "Cost Optimization", "Multi-Cloud Strategy"]
  },
  {
    icon: Layers,
    slug: "erp-crm",
    name: "ERP/CRM",
    description: "Streamline your operations with integrated enterprise systems. We implement and customize ERP and CRM solutions that align with your business processes.",
    features: ["ERP Implementation", "CRM Customization", "System Integration", "User Training & Adoption"]
  },
  {
    icon: DollarSign,
    slug: "cpq-enterprise-pricing",
    name: "CPQ/Enterprise Pricing",
    description: "Optimize your pricing and quoting processes. Our CPQ solutions help sales teams configure products, generate accurate quotes, and close deals faster.",
    features: ["Configure, Price, Quote Solutions", "Pricing Optimization", "Quote-to-Cash Automation", "Revenue Management"]
  },
  {
    icon: Shield,
    slug: "site-reliability-engineering",
    name: "Site Reliability Engineering",
    description: "Build and maintain highly reliable systems. Our SRE practices ensure your applications are scalable, resilient, and performant in production environments.",
    features: ["Reliability Engineering", "Incident Management", "Performance Optimization", "Infrastructure as Code"]
  },
  {
    icon: TestTube,
    slug: "quality-assurance-testing",
    name: "Quality Assurance & Testing",
    description: "Deliver software with confidence. Our comprehensive QA services ensure your applications meet the highest quality standards before reaching users.",
    features: ["Test Automation", "Performance Testing", "Security Testing", "QA Strategy & Planning"]
  },
  {
    icon: Users,
    slug: "dynamic-workforce-solutions",
    name: "Dynamic Workforce Solutions",
    description: "Scale your team with the right talent. We provide flexible staffing solutions to help you meet project demands and build high-performing teams.",
    features: ["Staff Augmentation", "Team Building", "Resource Planning", "Skill Development"]
  }
];

const engagementModels = [
  { name: "Fixed-Price Projects", description: "Clear scope, fixed budget, predictable outcomes." },
  { name: "Time and Materials", description: "Flexible engagement with pay-as-you-go billing." },
  { name: "Dedicated Teams", description: "Your own team of experts, fully committed to your success." },
  { name: "Proof of Concept", description: "Validate ideas quickly with focused implementations." },
  { name: "Managed Services", description: "Ongoing support and management of your technology." },
  { name: "Staff Augmentation", description: "Extend your team with skilled professionals." }
];

const deliveryModels = [
  { name: "Onsite", description: "Our team works at your location for maximum collaboration." },
  { name: "Offsite", description: "Remote delivery with regular touchpoints and updates." },
  { name: "Offshore", description: "Cost-effective delivery from our global delivery centers." },
  { name: "Hybrid", description: "Flexible combination tailored to your needs." }
];

export default function Services() {
  return (
    <div data-testid="services-page" className="page-transition">
      {/* Hero Section */}
      <section className="bg-white border-b border-slate-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16 lg:py-24">
          <div className="flex flex-col lg:flex-row lg:items-end lg:justify-between gap-8">
            <div className="max-w-3xl">
              <p className="text-xs font-bold uppercase tracking-[0.2em] text-blue-600 mb-4">
                Our Services
              </p>
              <h1 className="text-4xl sm:text-5xl lg:text-6xl font-black tracking-tighter leading-none text-slate-900 font-['Outfit'] mb-6">
                Technology Solutions That Drive Results
              </h1>
              <p className="text-lg text-slate-600 leading-relaxed font-['Plus_Jakarta_Sans']">
                From software development to digital transformation, we offer comprehensive 
                technology consulting services tailored to your business needs.
              </p>
            </div>
            
            {/* Services Dropdown */}
            <div className="flex-shrink-0">
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button 
                    variant="outline" 
                    className="border-slate-900 text-slate-900 hover:bg-slate-900 hover:text-white rounded-none px-6"
                    data-testid="services-dropdown-trigger"
                  >
                    Jump to Service
                    <ChevronDown className="ml-2 h-4 w-4" />
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end" className="w-64 max-h-80 overflow-y-auto">
                  {services.map((service) => (
                    <DropdownMenuItem key={service.slug} asChild>
                      <Link 
                        to={`/services/${service.slug}`}
                        className="cursor-pointer"
                        data-testid={`service-dropdown-${service.slug}`}
                      >
                        <service.icon className="mr-2 h-4 w-4" />
                        {service.name}
                      </Link>
                    </DropdownMenuItem>
                  ))}
                </DropdownMenuContent>
              </DropdownMenu>
            </div>
          </div>
        </div>
      </section>

      {/* Services Grid */}
      <section className="bg-slate-50 py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-px bg-slate-200">
            {services.map((service) => (
              <Link 
                key={service.slug}
                to={`/services/${service.slug}`}
                data-testid={`service-card-${service.slug}`}
                className="service-card bg-white p-8 lg:p-10 border border-slate-200 group"
              >
                <div className="flex items-start gap-4 mb-6">
                  <div className="w-12 h-12 bg-blue-100 flex items-center justify-center flex-shrink-0 group-hover:bg-blue-700 transition-colors">
                    <service.icon className="w-6 h-6 text-blue-700 group-hover:text-white transition-colors" />
                  </div>
                  <h3 className="text-xl lg:text-2xl font-semibold text-slate-900 font-['Outfit'] tracking-tight group-hover:text-blue-700 transition-colors">
                    {service.name}
                  </h3>
                </div>
                <p className="text-slate-600 leading-relaxed mb-6 font-['Plus_Jakarta_Sans']">
                  {service.description}
                </p>
                <ul className="space-y-2 mb-6">
                  {service.features.map((feature) => (
                    <li key={feature} className="flex items-center gap-2 text-sm text-slate-700">
                      <div className="w-1.5 h-1.5 bg-blue-600 rounded-full" />
                      {feature}
                    </li>
                  ))}
                </ul>
                <span className="inline-flex items-center text-blue-600 text-sm font-medium">
                  Learn more
                  <ArrowRight className="ml-2 h-4 w-4 group-hover:translate-x-1 transition-transform" />
                </span>
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* Engagement Models */}
      <section className="bg-white py-20 border-t border-slate-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-2xl mb-12">
            <p className="text-xs font-bold uppercase tracking-[0.2em] text-blue-600 mb-4">
              Flexible Options
            </p>
            <h2 className="text-3xl sm:text-4xl font-bold tracking-tight text-slate-900 font-['Outfit']">
              Engagement Models
            </h2>
            <p className="text-slate-600 mt-4 font-['Plus_Jakarta_Sans']">
              Choose the engagement model that best fits your project requirements and business objectives.
            </p>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {engagementModels.map((model) => (
              <div 
                key={model.name}
                className="p-6 border border-slate-200 hover:border-blue-600 transition-colors"
              >
                <h3 className="text-lg font-semibold text-slate-900 font-['Outfit'] mb-2">
                  {model.name}
                </h3>
                <p className="text-sm text-slate-600 font-['Plus_Jakarta_Sans']">
                  {model.description}
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Delivery Models */}
      <section className="bg-slate-900 py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-2xl mb-12">
            <p className="text-xs font-bold uppercase tracking-[0.2em] text-blue-400 mb-4">
              Global Delivery
            </p>
            <h2 className="text-3xl sm:text-4xl font-bold tracking-tight text-white font-['Outfit']">
              Service Delivery Models
            </h2>
            <p className="text-slate-400 mt-4 font-['Plus_Jakarta_Sans']">
              We offer flexible delivery options to meet your operational and budget requirements.
            </p>
          </div>
          
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
            {deliveryModels.map((model) => (
              <div 
                key={model.name}
                className="p-6 bg-slate-800 border border-slate-700"
              >
                <h3 className="text-lg font-semibold text-white font-['Outfit'] mb-2">
                  {model.name}
                </h3>
                <p className="text-sm text-slate-400 font-['Plus_Jakarta_Sans']">
                  {model.description}
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="bg-blue-700 py-16">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-2xl sm:text-3xl font-bold text-white font-['Outfit'] mb-4">
            Ready to Discuss Your Project?
          </h2>
          <p className="text-blue-100 mb-8 font-['Plus_Jakarta_Sans']">
            Let our experts help you find the right solution for your business challenges.
          </p>
          <Link to="/contact">
            <Button 
              data-testid="services-cta-button"
              className="bg-white text-blue-700 hover:bg-slate-100 rounded-none px-8"
            >
              Contact Us
              <ArrowRight className="ml-2 h-4 w-4" />
            </Button>
          </Link>
        </div>
      </section>
    </div>
  );
}
